import { t } from '../../../lib/i18n';
import { financePeriodTabLabel } from '../labels';
import type { FinancePeriod, FinanceTab } from '../types';
import { FINANCE_TABS, cn } from '../utils';

/**
 * The two tab strips of «Фінанси» — `Redesign-D-Admin{,-Mobile}`.
 *
 * They live in one file because they are the same object at two sizes, and
 * because the period strip MOVES between viewports: on desktop it sits in the
 * page header next to the actions, on mobile the header has no actions at all
 * and the strip is folded into the revenue card. Rendering it from one
 * component keeps the two placements from drifting apart.
 */

interface FinanceTabsProps {
  active: FinanceTab;
  counts: Record<FinanceTab, number>;
  onChange: (tab: FinanceTab) => void;
}

/**
 * «Усі / До оплати / Прострочено / Повернути депозит / Без боргів».
 *
 * A `cf-fill` trough with 34px pills from `lg` up; below that the row of
 * outlined chips that bleeds into the shell's 16px gutter (`-mx-4 px-4`), so a
 * partly-scrolled strip visibly continues off-screen rather than ending in a
 * hairline. Same shape the bids archive strip uses — one tab idiom per app.
 */
export function FinanceTabs({ active, counts, onChange }: FinanceTabsProps) {
  return (
    <div
      role="tablist"
      aria-label={t('adminFinance.tabsAria')}
      className="-mx-4 flex gap-1.5 overflow-x-auto px-4 lg:mx-0 lg:w-fit lg:gap-0.5 lg:overflow-visible lg:rounded-cf-lg lg:bg-cf-fill lg:p-[3px]"
    >
      {FINANCE_TABS.map((tab) => {
        const isActive = tab === active;
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab)}
            className={cn(
              'h-9 shrink-0 rounded-full border px-3 text-[13px] font-semibold whitespace-nowrap lg:h-[34px] lg:rounded-cf-md lg:border-0 lg:px-3',
              isActive
                ? 'border-cf-brand bg-cf-brand text-white'
                : 'border-cf-line bg-cf-surface text-cf-ink lg:bg-transparent',
            )}
          >
            {t(`adminFinance.tabs.${tab}`)}{' '}
            <span className="font-mono text-[11px] font-medium opacity-70 max-lg:hidden">
              {counts[tab]}
            </span>
            <span className="lg:hidden">· {counts[tab]}</span>
          </button>
        );
      })}
    </div>
  );
}

const PERIODS: FinancePeriod[] = ['month', 'quarter', 'year'];

interface FinancePeriodTabsProps {
  active: FinancePeriod;
  /** The instant the month/year captions are named from. */
  now?: Date;
  onChange: (period: FinancePeriod) => void;
}

/**
 * «Вересень / Квартал / 2026» — the revenue window.
 *
 * The month and the year are rendered from the CLOCK, not from a catalogue
 * literal. They used to be frozen strings («Вересень», «2026») that were right
 * on the day the artboard was drawn and wrong on every other one.
 */
export function FinancePeriodTabs({ active, now, onChange }: FinancePeriodTabsProps) {
  return (
    <div
      role="tablist"
      aria-label={t('adminFinance.periodAria')}
      className="flex gap-0.5 rounded-cf-md bg-cf-fill p-0.5 lg:rounded-cf-lg lg:p-[3px]"
    >
      {PERIODS.map((period) => {
        const isActive = period === active;
        return (
          <button
            key={period}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(period)}
            className={cn(
              'h-7 rounded-cf-sm px-[9px] text-[11px] font-semibold whitespace-nowrap lg:h-[34px] lg:rounded-cf-md lg:px-3 lg:text-[13px]',
              isActive ? 'bg-cf-brand text-white' : 'text-cf-ink',
            )}
          >
            {financePeriodTabLabel(period, now)}
          </button>
        );
      })}
    </div>
  );
}
